import { api, get } from './api'
import { configureSound } from './sound'

/**
 * Which look the app wears. 'island' is the original look; 'marketing' matches the launch advertisement and has five
 * cartoon backdrops; 'halloween' is the seasonal one. Everyone picks their own, at sign-up and on the Account page; the
 * choice belongs to that account alone, and a visitor who is not signed in sees 'island'.
 */
export type Look = 'island' | 'marketing' | 'halloween'
export type Backdrop = 'shore' | 'bubbles' | 'town' | 'sunset' | 'palms'
export type LookState = { look: Look; backdrop: Backdrop; sound: boolean }
/** The seven things the picker offers. */
export type LookChoice = 'island' | Backdrop | 'halloween'

export const choiceOf = (s: Pick<LookState, 'look' | 'backdrop'>): LookChoice => (s.look === 'marketing' ? s.backdrop : s.look)
export const choiceToPatch = (c: LookChoice): { look: Look; backdrop?: Backdrop } => (c === 'island' || c === 'halloween' ? { look: c } : { look: 'marketing', backdrop: c })

/** Fades out the logo screen from index.html. Safe to call twice. */
export function hideSplash(): void {
  const el = document.getElementById('splash')
  if (!el) return
  el.classList.add('done')
  window.setTimeout(() => el.remove(), 400)
}

/** The look is remembered on this device so the next refresh starts with it (public/boot.js reads this key). */
function rememberLook(state: Pick<LookState, 'look' | 'backdrop'> | null): void {
  try {
    if (state && state.look !== 'island') localStorage.setItem('vc-look', JSON.stringify({ look: state.look, backdrop: state.backdrop }))
    else localStorage.removeItem('vc-look')
  } catch { /* storage can be blocked; the look still applies once the account loads */ }
}

export function applyLook(state: Pick<LookState, 'look' | 'backdrop' | 'sound'> | null): void {
  const root = document.documentElement
  rememberLook(state)
  if (state && state.look !== 'island') {
    root.dataset.look = state.look
    root.dataset.backdrop = state.backdrop
  } else {
    delete root.dataset.look
    delete root.dataset.backdrop
  }
  configureSound(Boolean(state?.sound), state?.look ?? 'island')
}
export const fetchLook = () => get<LookState>('/api/market/look')
export const saveLook = (patch: { look?: Look; backdrop?: Backdrop; sound?: boolean }) => api<LookState>('/api/market/look', { method: 'POST', body: JSON.stringify(patch) })
